import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY } from '../content/seo';

/**
 * Renders <head> meta tags via Helmet (title, description, canonical, OG, Twitter)
 * and imperatively injects JSON-LD <script> tags via a useEffect side-channel
 * (Helmet v1 does not reliably accept multiple mapped script children).
 */
export default function SEOHead({
  title,
  description,
  path = '',
  image,
  jsonLd,
  noindex = false,
}) {
  const url = `${COMPANY.site}${path}`;
  const finalTitle = title
    ? `${title} · ${COMPANY.name}`
    : `${COMPANY.name} — ${COMPANY.slogan}`;
  const finalDescription = description || COMPANY.description;
  const finalImage = image || `${COMPANY.site}/og-cover.jpg`;

  const jsonLdBlocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  useEffect(() => {
    // Remove any prior injected JSON-LD from previous route
    document
      .querySelectorAll('script[data-seo-json-ld="1"]')
      .forEach((n) => n.remove());
    jsonLdBlocks.forEach((block) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo-json-ld', '1');
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
    return () => {
      document
        .querySelectorAll('script[data-seo-json-ld="1"]')
        .forEach((n) => n.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(jsonLdBlocks)]);

  return (
    <Helmet title={String(finalTitle)}>
      <meta name="description" content={String(finalDescription)} />
      <link rel="canonical" href={String(url)} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      <meta property="og:title" content={String(finalTitle)} />
      <meta property="og:description" content={String(finalDescription)} />
      <meta property="og:url" content={String(url)} />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={String(finalImage)} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={String(finalTitle)} />
      <meta name="twitter:description" content={String(finalDescription)} />
      <meta name="twitter:image" content={String(finalImage)} />
    </Helmet>
  );
}
